// oxlint-disable import/no-unassigned-import -- core-js entry modules install required runtime methods before hydration.
;globalThis["_sentryRouteManifest"] = "{\"dynamicRoutes\":[{\"path\":\"/survey/:locale\",\"regex\":\"^/survey/([^/]+)$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/project/:id\",\"regex\":\"^/survey/([^/]+)/project/([^/]+)$\",\"paramNames\":[\"locale\",\"id\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/project/:id/analytics\",\"regex\":\"^/survey/([^/]+)/project/([^/]+)/analytics$\",\"paramNames\":[\"locale\",\"id\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/manager-embed/project/:id\",\"regex\":\"^/survey/([^/]+)/manager-embed/project/([^/]+)$\",\"paramNames\":[\"locale\",\"id\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/dev/responder\",\"regex\":\"^/survey/([^/]+)/dev/responder$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/free-survey/project/:id\",\"regex\":\"^/survey/([^/]+)/free-survey/project/([^/]+)$\",\"paramNames\":[\"locale\",\"id\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/free-survey/import\",\"regex\":\"^/survey/([^/]+)/free-survey/import$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/free-survey/preview\",\"regex\":\"^/survey/([^/]+)/free-survey/preview$\",\"paramNames\":[\"locale\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/project-videos/:id\",\"regex\":\"^/survey/([^/]+)/project-videos/([^/]+)$\",\"paramNames\":[\"locale\",\"id\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/skill-result/:id\",\"regex\":\"^/survey/([^/]+)/skill-result/([^/]+)$\",\"paramNames\":[\"locale\",\"id\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/insight/:id\",\"regex\":\"^/survey/([^/]+)/insight/([^/]+)$\",\"paramNames\":[\"locale\",\"id\"],\"hasOptionalPrefix\":true},{\"path\":\"/survey/:locale/embed/project/:id\",\"regex\":\"^/survey/([^/]+)/embed/project/([^/]+)$\",\"paramNames\":[\"locale\",\"id\"],\"hasOptionalPrefix\":true}],\"staticRoutes\":[],\"isrRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.4.2";import * as Sentry from '@sentry/nextjs';
import 'core-js/es/array/find-last';
import 'core-js/es/array/find-last-index';
import 'core-js/es/array/to-reversed';
import 'core-js/es/array/to-sorted';
import 'core-js/es/array/to-spliced';
import 'core-js/es/array/with';

import { isLocalhost } from '@/shared-utilities/environment';
import { containsSensitiveText } from '@/shared-utilities/sensitive-text';
import {
  SENTRY_DATA_COLLECTION,
  scrubDynamicSamplingContext,
  scrubStreamedSpanFields,
  scrubUrl,
} from '@/shared-utilities/sentry-data-collection';

const isDev =
  typeof window !== 'undefined' && window.location.host.includes('dev');
const isLocalDev = typeof window !== 'undefined' && isLocalhost();

const scrubSentryEvent = <E extends Sentry.Event>(event: E): E | null => {
  if (containsSensitiveText(event.message)) {
    return null;
  }

  if (
    event.exception?.values?.some((error) => containsSensitiveText(error.value))
  ) {
    return null;
  }

  delete event.user;

  if (event.request) {
    event.request.url = scrubUrl(event.request.url);
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.data;
    delete event.request.query_string;
  }

  event.breadcrumbs = event.breadcrumbs?.filter(
    (breadcrumb) =>
      !breadcrumb.category?.startsWith('ui.') &&
      breadcrumb.category !== 'console' &&
      !containsSensitiveText(breadcrumb.message) &&
      !containsSensitiveText(breadcrumb.data)
  );

  return event;
};

const SUPPRESSED_ERROR_PATTERNS = {
  DISCONNECTED_PORT: /.*Attempting to use a disconnected port object.*/i,
  FRAME: /.*Blocked a frame with origin.*/i,
  EXTENSION: {
    SERVICE_WORKER: /.*extensionServiceWorker\.js.*/i,
    TALISMAN: /.*Talisman extension.*/i,
    GENERIC: /.*Extension.*/i,
    RUNTIME_MESSAGE: /.*runtime\.sendMessage.*/i,
    WORKER: /.*Uncaught exception in worker.*/i,
  },
  UNEXPECTED_TOKEN: /.*SyntaxError: Unexpected token '{'*/i,
  SURVEY_URLS_ALLOWED: /.*survey URLs are allowed.*/i,
  ABORT_ERROR:
    /(^AbortError:|signal is aborted without reason|The user aborted a request)/i,
  FETCH_FAILED: /^TypeError: Failed to fetch/i,
};

const isErrorSuppressed = (errorValue: string | undefined): boolean => {
  if (!errorValue) return false;

  return (
    SUPPRESSED_ERROR_PATTERNS.DISCONNECTED_PORT.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.FRAME.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.SERVICE_WORKER.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.TALISMAN.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.GENERIC.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.RUNTIME_MESSAGE.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.WORKER.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.UNEXPECTED_TOKEN.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.SURVEY_URLS_ALLOWED.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.ABORT_ERROR.test(errorValue) ||
    SUPPRESSED_ERROR_PATTERNS.FETCH_FAILED.test(errorValue)
  );
};

Sentry.init({
  dsn: process.env.NEXT_PUBLIC_SENTRY_SURVEY_DSN,

  enabled: !isLocalDev,
  environment: isDev ? 'dev' : 'prod',

  // Enhanced integrations
  /* oxlint-disable import/namespace */
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
  ],
  /* oxlint-enable import/namespace */
  dataCollection: SENTRY_DATA_COLLECTION,

  release: process.env.NEXT_PUBLIC_SHORT_SHA,

  ignoreErrors: [
    SUPPRESSED_ERROR_PATTERNS.DISCONNECTED_PORT,
    SUPPRESSED_ERROR_PATTERNS.FRAME,
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.SERVICE_WORKER,
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.TALISMAN,
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.GENERIC,
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.RUNTIME_MESSAGE,
    SUPPRESSED_ERROR_PATTERNS.EXTENSION.WORKER,
    SUPPRESSED_ERROR_PATTERNS.SURVEY_URLS_ALLOWED,
    SUPPRESSED_ERROR_PATTERNS.ABORT_ERROR,
    SUPPRESSED_ERROR_PATTERNS.FETCH_FAILED,
  ],

  // Drop browser-extension noise and the bundled posthog lazy chunk (app:///executors/<n>.js).
  denyUrls: [
    /chrome-extension:\/\//i,
    /moz-extension:\/\//i,
    /safari-extension:\/\//i,
    /executors\/\d+\.js/i,
  ],

  beforeSend(event, hint) {
    const { originalException } = hint;

    if (event.exception) {
      const errorValues = event.exception.values?.[0];
      if (errorValues) {
        const isSuppressed = event.exception.values?.some((error) =>
          isErrorSuppressed(error.value)
        );

        Sentry.addBreadcrumb({
          category: isSuppressed ? 'sentry.suppressed' : 'sentry.error',
          message: errorValues.value,
          level: isSuppressed ? 'info' : 'error',
          data: {
            errorType: errorValues.type,
            environment: isDev ? 'development' : 'production',
            url:
              typeof window !== 'undefined' ? window.location.href : 'unknown',
            stack: errorValues.stacktrace?.frames?.map((frame) => ({
              filename: frame.filename,
              function: frame.function,
              line: frame.lineno,
              column: frame.colno,
            })),
            isSuppressed,
            errorCategory: isSuppressed ? 'suppressed' : 'active',
            timestamp: new Date().toISOString(),
          },
        });
      }
    }

    if (
      typeof originalException === 'object' &&
      originalException !== null &&
      'extraInfo' in originalException
    ) {
      const { extraInfo } = originalException;
      if (typeof extraInfo === 'object' && extraInfo !== null) {
        event.extra = { ...event.extra, ...extraInfo };
      }
    }

    return scrubSentryEvent(event);
  },

  beforeSendSpan: scrubStreamedSpanFields,

  beforeBreadcrumb(breadcrumb) {
    if (
      breadcrumb.category?.startsWith('ui.') ||
      breadcrumb.category === 'console' ||
      containsSensitiveText(breadcrumb.message) ||
      containsSensitiveText(breadcrumb.data)
    ) {
      return null;
    }

    if (breadcrumb.data && 'url' in breadcrumb.data) {
      breadcrumb.data.url = scrubUrl(String(breadcrumb.data.url));
    }

    return breadcrumb;
  },

  // No cookie-consent banner exists in survey, so replay and tracing both
  // run on the same cookieless basis as error capture, not gated on consent.
  // Sessions are never sampled at random: replay stays in buffer mode and is
  // only uploaded when something goes wrong, which is what support needs and
  // keeps quota flat under respondent traffic.
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,

  tracesSampleRate: isDev ? 1.0 : 0.1,
  tracePropagationTargets: [
    /^https:\/\/[^/]*\.theysaid\.io\/(survey\/)?graphql/,
  ],
});

Sentry.getClient()?.on('createDsc', scrubDynamicSamplingContext);

export const onRouterTransitionStart = (
  Sentry as { captureRouterTransitionStart?: unknown }
).captureRouterTransitionStart;
